import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"

const useValidateAddress = (geocoder, form, setCenterLatLng, setMeetingLatLng, setIsButtonDisabled, setButtonLabel) => {
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [suggestedAddress, setSuggestedAddress] = useState("")
  const [suggestedComponents, setSuggestedComponents] = useState(null)
  const { t } = useTranslation()

  const match = (component, value) => {
    if (!component || value == null) return false
    const valueLower = String(value).toLowerCase().trim()
    const longName = (component.long_name || "").toLowerCase().trim()
    const shortName = (component.short_name || "").toLowerCase().trim()
    return longName === valueLower || shortName === valueLower
  }

  const normalizeAddress = (value) => String(value || "").toLowerCase().replace(/\s+/g, " ").trim()
  const getFirstSegment = (value) => String(value || "").split(",")[0].trim()

  const getComponent = (addressComponents, types) => {
    const component = addressComponents.find((comp) => types.some((t) => comp.types.includes(t)))
    return component ? { long_name: component.long_name, short_name: component.short_name } : null
  }

  const getComponents = (addressComponents, types) =>
    addressComponents
      .filter((comp) => types.some((t) => comp.types.includes(t)))
      .map((comp) => ({ long_name: comp.long_name, short_name: comp.short_name }))

  const validateAddressComponents = useCallback((place, values) => {
    const { street_address = "", city = "", state = "", country = "" } = values
    const addressComponents = place.address_components ?? []
    const streetAddressLower = String(street_address || "")
      .toLowerCase()
      .trim()

    const stateComponent = getComponent(addressComponents, ["administrative_area_level_1"])
    const countryComponent = getComponent(addressComponents, ["country"])
    // Check all city-level components — Google may return sublocality before the district name
    const cityComponents = getComponents(addressComponents, [
      "locality",
      "sublocality",
      "sublocality_level_1",
      "administrative_area_level_1",
      "administrative_area_level_2",
      "administrative_area_level_3"
    ])
    // Extract street components using short_name
    const streetNumber = getComponent(addressComponents, ["street_number"])
    const route = getComponent(addressComponents, ["route"])
    const geocodedStreet = [streetNumber?.short_name, route?.short_name].filter(Boolean).join(" ").toLowerCase().trim()

    return {
      isStreetAddress: streetAddressLower && geocodedStreet ? streetAddressLower === geocodedStreet : false,
      isZipValid: true,
      isCityValid: cityComponents.some((comp) => match(comp, city)),
      isStateValid: match(stateComponent, state),
      isCountryValid: match(countryComponent, country)
    }
  }, [])

  const handleConfirmAddress = useCallback(async () => {
    setLoading(true)
    setErrorMessage("")
    setSuggestedAddress("")
    setSuggestedComponents(null)
    try {
      const values = await form.validateFields(["street_address", "city", "state", "zip_code", "country"])
      console.log("[ListingLocation] Confirm address submitted", { values })

      const { street_address, city, state, zip_code, country } = values
      const userAddress = [street_address, city, state, zip_code, country].filter(Boolean).join(", ")
      const result = await Promise.race([
        new Promise((resolve) => {
          try {
            geocoder({ address: userAddress }, (place) => {
              try {
                if (place) {
                  console.log("[ListingLocation] Geocoder response", {
                    formattedAddress: place?.formatted_address,
                    addressComponents: place?.address_components
                  })
                  const validationResults = validateAddressComponents(place, values)
                  const isValid =
                    validationResults.isCityValid &&
                    validationResults.isStateValid &&
                    validationResults.isCountryValid

                  const formatted_address = place.formatted_address ?? ""
                  const streetValue = normalizeAddress(getFirstSegment(values.street_address))
                  const formattedValue = normalizeAddress(getFirstSegment(formatted_address))
                  const suggestedValue = normalizeAddress(getFirstSegment(suggestedAddress))
                  // Only enforce street match when the geocoder returned a numbered street;
                  // area/landmark addresses have no street_number so we skip the check.
                  const addressComponents = place.address_components ?? []
                  const hasStreetNumber = addressComponents.some((c) => c.types.includes("street_number"))
                  const normalizedFormatted = normalizeAddress(formatted_address)
                  // For numbered streets, street segment must match exactly.
                  // For area/landmark addresses (no street_number), the user's input must
                  // appear somewhere in the full formatted address — catches gibberish like "qwertyuiop".
                  const isStreetMatch = hasStreetNumber
                    ? formattedValue === streetValue || (suggestedValue && suggestedValue === streetValue)
                    : !streetValue || normalizedFormatted.includes(streetValue)

                  console.log("[ListingLocation] Address validation details", {
                    validationResults,
                    isValid,
                    streetValue,
                    formattedValue,
                    suggestedValue,
                    hasStreetNumber,
                    isStreetMatch
                  })

                  if (isValid && isStreetMatch) {
                    const location = place.geometry.location
                    setMeetingLatLng(formatted_address)
                    setCenterLatLng({ lat: location.lat(), lng: location.lng() })
                    setIsButtonDisabled(true)
                    setErrorMessage("")
                    setSuggestedAddress("")
                    setSuggestedComponents(null)
                    setButtonLabel(t("listings.tabs.location.confirmed"))
                    resolve(true)
                  } else {
                    const location = place.geometry?.location
                    if (location) setCenterLatLng?.({ lat: location.lat(), lng: location.lng() })
                    else setCenterLatLng?.(null)
                    setMeetingLatLng(null)
                    setButtonLabel(t("listings.tabs.location.confirm_address"))
                    setErrorMessage(t("listings.tabs.location.validation.address_not_verified"))
                    setSuggestedAddress(formatted_address)

                    const addressComponents = place.address_components ?? []
                    const streetNumber = getComponent(addressComponents, ["street_number"])
                    const route = getComponent(addressComponents, ["route"])
                    const premise = getComponent(addressComponents, ["premise"])
                    const subpremise = getComponent(addressComponents, ["subpremise"])
                    let suggestedStreet = [streetNumber?.short_name, route?.short_name].filter(Boolean).join(" ")
                    if (!suggestedStreet) suggestedStreet = getFirstSegment(formatted_address)
                    if (subpremise?.short_name) suggestedStreet = [subpremise.short_name, suggestedStreet].filter(Boolean).join(", ")
                    else if (premise?.short_name && !suggestedStreet.includes(premise.short_name)) suggestedStreet = [premise.short_name, suggestedStreet].filter(Boolean).join(", ")

                    const cityComp = getComponent(addressComponents, ["locality"]) ||
                      getComponent(addressComponents, ["postal_town"]) ||
                      getComponent(addressComponents, ["sublocality_level_1"]) ||
                      getComponent(addressComponents, ["administrative_area_level_2"]) ||
                      getComponent(addressComponents, ["administrative_area_level_3"])
                    const state = getComponent(addressComponents, ["administrative_area_level_1"])
                    const country = getComponent(addressComponents, ["country"])
                    const zip = getComponent(addressComponents, ["postal_code"])
                    const zipValue = zip?.short_name || zip?.long_name || ""
                    setSuggestedComponents({
                      street_address: suggestedStreet,
                      city: cityComp?.long_name || cityComp?.short_name || "",
                      state: state?.short_name || state?.long_name || "",
                      country: country?.short_name || country?.long_name || "",
                      ...(zipValue && { zip_code: zipValue })
                    })
                    resolve(false)
                  }
                } else {
                  setCenterLatLng?.(null)
                  setMeetingLatLng(null)
                  setButtonLabel(t("listings.tabs.location.confirm_address"))
                  setErrorMessage(t("listings.tabs.location.validation.address_not_verified"))
                  resolve(false)
                }
              } catch (innerErr) {
                console.error(innerErr)
                setCenterLatLng?.(null)
                setMeetingLatLng(null)
                setButtonLabel(t("listings.tabs.location.confirm_address"))
                resolve(false)
              }
            })
          } catch (outerErr) {
            console.error(outerErr)
            setCenterLatLng?.(null)
            setMeetingLatLng(null)
            setButtonLabel(t("listings.tabs.location.confirm_address"))
            resolve(false)
          }
        }),
        new Promise((resolve) => setTimeout(() => resolve(false), 10000))
      ])
      return result
    } catch (err) {
      console.error(err)
      return false
    } finally {
      setTimeout(() => {
        setLoading(false)
      }, 1000)
    }
  }, [form, geocoder, validateAddressComponents, setCenterLatLng, setMeetingLatLng, setIsButtonDisabled, setButtonLabel, suggestedAddress, t])

  const clearError = useCallback(() => {
    setErrorMessage("")
    setSuggestedAddress("")
    setSuggestedComponents(null)
  }, [])

  return { handleConfirmAddress, loading, errorMessage, suggestedAddress, suggestedComponents, clearError }
}

export default useValidateAddress
